import apiClient from '@/services/apiClient';
import type { ApiResponse } from '@/types/api';
import type {
  User,
  LoginRequest,
  RegisterRequest,
  AuthResult,
  AdminUser,
} from '@/types/auth';

/** 用户登录（access token 在响应体，refresh token 走 httpOnly cookie） */
export async function login(payload: LoginRequest) {
  const { data } = await apiClient.post<ApiResponse<AuthResult>>(
    '/auth/login',
    payload,
  );
  return data.data;
}

/** 用户注册 */
export async function register(payload: RegisterRequest) {
  const { data } = await apiClient.post<
    ApiResponse<{ user: User; access_token: string; token_type: string }>
  >('/auth/register', payload);
  return data.data;
}

/** 用户登出（只清除用户 refresh cookie，不影响管理员登录状态） */
export async function logout(accessToken?: string) {
  const { data } = await apiClient.post<ApiResponse<null>>(
    '/auth/logout',
    undefined,
    accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined,
  );
  return data.data;
}

/** 刷新 Access Token（使用 httpOnly cookie，无需传 refresh token） */
export async function refreshToken() {
  const { data } = await apiClient.post<ApiResponse<AuthResult>>(
    '/auth/refresh',
    {},
  );
  return data.data;
}

/** 请求密码重置（发送邮件） */
export async function requestPasswordReset(email: string) {
  const { data } = await apiClient.post<ApiResponse<null>>(
    '/auth/password-reset',
    { email },
  );
  return data.data;
}

/** 确认密码重置 */
export async function confirmPasswordReset(payload: {
  token: string;
  new_password: string;
}) {
  const { data } = await apiClient.post<ApiResponse<null>>(
    '/auth/password-reset/confirm',
    payload,
  );
  return data.data;
}

/**
 * OAuth 跳转：获取 provider 授权 URL。CSRF state 由后端生成并存入 Redis，
 * 已包含在返回的 redirect_url 中；`redirect` 为登录后返回的站内路径。
 */
export async function getOAuthRedirectUrl(provider: string, redirect?: string) {
  const { data } = await apiClient.get<ApiResponse<{ redirect_url: string }>>(
    `/auth/social/${provider}`,
    { params: redirect ? { redirect } : undefined },
  );
  return data.data;
}

/** 获取当前用户信息 */
export async function getUserInfo(accessToken?: string) {
  const { data } = await apiClient.get<ApiResponse<User>>(
    '/users/me',
    accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined,
  );
  return data.data;
}

/** 更新当前用户个人资料 */
export async function updateProfile(
  payload: Partial<Pick<User, 'nickname' | 'avatar_url' | 'language_preference' | 'traveler_type_id'>>,
) {
  const { data } = await apiClient.put<ApiResponse<User>>(
    '/users/me',
    payload,
  );
  return data.data;
}

/** 管理员登录（refresh token 走管理员专用的 httpOnly cookie） */
export async function adminLogin(payload: LoginRequest) {
  const { data } = await apiClient.post<
    ApiResponse<{ access_token: string; admin: AdminUser }>
  >('/admin/auth/login', payload);
  return data.data;
}

/** 管理员登出（只清除管理员 refresh cookie，不影响用户登录状态） */
export async function adminLogout(accessToken?: string) {
  const { data } = await apiClient.post<ApiResponse<null>>(
    '/admin/auth/logout',
    undefined,
    accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined,
  );
  return data.data;
}
